<template>
  <div
    class="task-row"
    :class="{ selected, done: isDone(task), urgent: !!task.actionUrl && !isDone(task) && !compact, compact }"
    role="button"
    tabindex="0"
    @click="emit('open', task)"
    @keydown.enter="emit('open', task)"
  >
    <div class="flex gap-2.5 items-start">
      <TaskCheckbox :checked="isDone(task)" :small="compact" :disabled="busy" class="mt-px" @toggle="emit('toggle', task)" />
      <div class="flex-1 min-w-0 flex flex-col gap-1">
        <span class="title" :class="{ 'line-through text-surface-400': isDone(task) }">{{ task.title }}</span>
        <span v-if="!compact && task.notes && !isDone(task)" class="text-[13px] text-surface-600 leading-snug line-clamp-2 whitespace-pre-line">{{ task.notes }}</span>
        <div v-if="!compact" class="flex gap-1.5 flex-wrap items-center">
          <span v-if="chip" class="chip chip-auto">{{ chip }}</span>
          <span v-if="task.customerName && !hideCustomer" class="chip chip-customer">{{ task.customerName }}</span>
          <span v-if="meta" class="text-xs" :class="isOverdue(task) ? 'text-red-700' : 'text-surface-500'">{{ meta }}</span>
        </div>
        <span v-else-if="compactMeta" class="text-xs" :class="isOverdue(task) ? 'text-red-700' : 'text-surface-500'">{{ compactMeta }}</span>
      </div>
      <span v-if="compact && task.autoType && !isDone(task)" class="chip chip-auto flex-none">{{ TASK_AUTO_TYPE_LABELS[task.autoType] }}</span>
    </div>
    <a
      v-if="task.actionUrl && !compact && !isDone(task)"
      :href="task.actionUrl"
      target="_blank"
      rel="noopener"
      class="action-link"
      @click.stop
    >{{ task.actionLabel || 'פתיחה' }}</a>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { type ITask, TASK_AUTO_TYPE_LABELS } from '@torim-app/types';
import TaskCheckbox from './TaskCheckbox.vue';
import { dueLabel, isDone, isOverdue, recurrenceLabel, sourceChip } from './task-utils';

const props = defineProps<{
  task: ITask;
  /** Dense one-line variant for the calendar strip / sidebar. */
  compact?: boolean;
  selected?: boolean;
  busy?: boolean;
  /** Inside a customer's profile the customer chip is noise. */
  hideCustomer?: boolean;
  showAssignee?: boolean;
}>();

const emit = defineEmits<{
  toggle: [task: ITask];
  open: [task: ITask];
}>();

const chip = computed(() => sourceChip(props.task));

const meta = computed(() => {
  const parts: string[] = [];
  if (isDone(props.task)) {
    if (props.task.completedByName) parts.push(`בוצע ע״י ${props.task.completedByName}`);
  } else {
    parts.push(isOverdue(props.task) ? `יעד: ${dueLabel(props.task)}` : dueLabel(props.task));
    const recurrence = recurrenceLabel(props.task);
    if (recurrence) parts.push(recurrence);
  }
  if (props.showAssignee) parts.push(props.task.assigneeName ? `באחריות ${props.task.assigneeName}` : 'באחריות בעל העסק');
  return parts.join(' · ');
});

const compactMeta = computed(() => {
  if (isDone(props.task)) return '';
  if (isOverdue(props.task)) return `מ${dueLabel(props.task)}`;
  if (props.task.reservation) return dueLabel(props.task).replace(/^היום · /, '');
  return props.task.dueTime != null ? dueLabel(props.task).replace(/^היום · /, '') : '';
});
</script>

<style scoped lang="scss">
.task-row {
  @apply bg-white border border-surface-200 rounded-[14px] p-3 flex flex-col gap-2.5 cursor-pointer transition-colors text-start;

  &:hover {
    @apply border-surface-300;
  }

  &:focus-visible {
    @apply outline-none ring-2 ring-primary-300;
  }

  &.selected {
    @apply border-primary border-2;
    padding: calc(0.75rem - 1px);
  }

  &.urgent {
    border-color: #f3c9a8;
  }

  &.compact {
    @apply border-0 rounded-none p-0 py-2 bg-transparent;
    min-height: 40px;
    justify-content: center;
  }

  .title {
    @apply text-[15px] font-semibold text-surface-900 leading-snug;
  }

  &.compact .title {
    @apply text-sm font-medium;
  }
}

.chip {
  @apply text-[11px] font-semibold rounded-full px-2 py-0.5 leading-4;
}

.chip-auto {
  background: #ffedd5;
  color: #9a3412;
}

.chip-customer {
  background: #d9f3f4;
  color: #0b5d65;
}

.action-link {
  @apply h-10 rounded-[10px] bg-primary-50 text-primary-700 text-sm font-semibold no-underline flex items-center justify-center;
}
</style>
